/**
 * The drill's trail and its links — `docs/adr/6006`; drawn by template D's `DrillHeader` since
 * 2026-09-30 (`docs/adr/9032`).
 *
 * The chrome's bar already names the chapter and the screen from the nav model (for a drill
 * page, "In-season / Allocation", by longest match). This is the level path BELOW that — the
 * allocation screen, then the store, then the article — with each level a link that carries
 * the run, so the back button and a pasted link walk the same trail (G2.2).
 *
 * The first crumb's label is the nav model's own key for the allocation screen, held to the
 * manifest's entry by a test, so the trail cannot call the level above by a different name
 * from the rail.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiCollection, useFamilyLabel } from '@/api';
import { inseasonListProposals, inseasonListRuns } from '@/api/generated';
import { DRILL_CONSTANT_KEYS, DRILL_KICKER_KEYS, DrillHeader, RunWords, type DrillCrumb } from '@/planning';
import { FOCUS_RING } from '@/primitives';

import {
  ALLOCATION_PATH, articlePath, carriesDepartment, departmentPath, drillHref, drillQuery, readDepartment, readDepartmentParam, readDrill,
  storePath, type AllocationDrill,
} from './drill';
import { useEntityName, useRunNames, withCode } from './run-names';
import { navigateToScope } from './scope';

/** The keys the address bar names, following back and forward — `useInSeasonScope`'s shape. */
export function useAllocationDrill(): AllocationDrill {
  const [, setNavigations] = useState(0);
  useEffect(() => {
    const onPopState = (): void => setNavigations((count) => count + 1);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);
  const pathname = typeof window === 'undefined' ? '' : window.location.pathname;
  return useMemo(() => readDrill(pathname), [pathname]);
}

/**
 * The department the address names — the family of a department page, or the `?department=` a
 * store or article page was reached through (ADR 6102). `null` when neither.
 */
export function useDrillDepartment(): string | null {
  const [, setNavigations] = useState(0);
  useEffect(() => {
    const onPopState = (): void => setNavigations((count) => count + 1);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);
  if (typeof window === 'undefined') return null;
  return readDepartment(window.location.pathname) ?? readDepartmentParam(window.location.search);
}

/** The `?department=` of the current address, read at click time — see `DrillLink`. */
function currentDepartmentParam(): string | null {
  return typeof window === 'undefined' ? null : readDepartmentParam(window.location.search);
}

/**
 * The menu's label for the allocation screen — «Proposte», the manifest's own view of it, which the
 * Replenishment rail draws as an entry of its own since the owner ruling of 2026-10-24 (the parent
 * «Allocazione e riassortimento» is no longer in the menu). Spelled once here because importing
 * the manifest would pull every screen it lazily names into this module's closure. `drill.test.ts`
 * holds the two to the same key.
 */
export const ALLOCATION_NAV_LABEL_KEY = 'inseason.nav.allocationProposals';

/** Follow a drill link inside the app: a history entry, the run carried (`navigateToScope`). */
export function followDrill(path: string, runId: string | null, department: string | null = null): void {
  navigateToScope(new URLSearchParams(drillQuery(runId, department)), path);
}

/**
 * An anchor to a drill level. A real `href` (so a middle click and "copy link" give the
 * shareable URL), and a plain click stays in the app.
 *
 * A link to a store or article level keeps the department the current page was reached
 * through (`?department=`, ADR 6102) unless `department` names one explicitly, so the trail
 * still shows the family one level further down without every page having to pass it on.
 */
export function DrillLink({
  path, runId, children, labelKey, labelValues, department,
}: {
  readonly path: string;
  readonly runId: string | null;
  readonly children: React.ReactNode;
  /** An accessible name when the visible text is a bare key (a store or an article code). */
  readonly labelKey?: string;
  readonly labelValues?: Readonly<Record<string, string>>;
  /** The family this link is taken through; omitted, the current `?department=` is kept. */
  readonly department?: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const through = carriesDepartment(path) ? (department === undefined ? currentDepartmentParam() : department) : null;
  return (
    <a
      href={drillHref(path, runId, through)}
      className={`text-ink-primary rounded-control underline underline-offset-2 ${FOCUS_RING}`}
      {...(labelKey === undefined ? {} : { 'aria-label': t(labelKey, labelValues ?? {}) })}
      onClick={(event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        followDrill(path, runId, through);
      }}
    >
      {children}
    </a>
  );
}

/**
 * The drill's header — template D (the designer's brief part 2 §5, `docs/adr/9032`, 2026-09-30):
 * the breadcrumb band whose first crumb is «Riassortimento · Allocazione», the ancestors as links
 * carrying the run (and the family a store or article was reached through), the entity as the last
 * crumb and the title, its kicker, and the drill's constants — Mercato · Run · the parent.
 *
 * **One way up.** The breadcrumb is the only way back; no page under it draws a «torna a» link or
 * arrows between siblings. **A store or an article is named** by the run's replenishment report,
 * else by the network-health read (`useEntityName`, usability audit B1, 2026-10-02): the title,
 * the crumbs and the parent say «name (code)». Where the platform publishes no name its
 * code stands in the title, in mono, with «Nome in arrivo» — never a blank; the code shows first
 * and the name joins it when the read answers. A department's family is the name it is
 * published under.
 *
 * `storeKey` `null` is the department page itself; `departmentFamily` `null` is a store reached
 * without one (the Cockpit's top stores, the allocation drawer).
 */
export function DrillTrail({
  runId, storeKey, articleKey, departmentFamily = null, leafKey = null,
}: {
  readonly runId: string | null;
  readonly storeKey: string | null;
  readonly articleKey?: string | null;
  readonly departmentFamily?: string | null;
  /**
   * A page below the article — its sales history (client rule 3, ADR 6006 amendment
   * 2026-09-30). The article becomes a link one level up and this is the entity.
   */
  readonly leafKey?: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const familyLabel = useFamilyLabel();
  const runs = useApiCollection(inseasonListRuns, { query: {} }, { enabled: runId !== null });
  const market = runId === null ? null : runs.data?.find((row) => row.run_id === runId)?.country_key ?? null;
  const article = storeKey === null ? null : articleKey ?? null;
  // The lines the page itself reads for this store (and article): they name their own keys
  // (bata-platform ADR 6080), so the header says what the table beside it says. The query is the
  // page's own — one cache entry, no extra request. Nothing is read for the names until it has
  // answered: the report walk is for a key no line names, never for a page whose lines are on the
  // way (audit 2, D: «Nome in arrivo» over a table that already named the store).
  const bounded = runId !== null && storeKey !== null;
  const held = useApiCollection(
    inseasonListProposals,
    { query: bounded ? { run_id: runId, store_key: storeKey, ...(article === null ? {} : { article_key: article }) } : {} },
    { enabled: bounded },
  );
  const namesFor = bounded && !held.isPending ? runId : null;
  const names = useRunNames(namesFor, { replenishment: true, ...(held.data === undefined ? {} : { lines: held.data }) });
  const storeName = useEntityName(namesFor, names, 'store', storeKey);
  const articleName = useEntityName(namesFor, names, 'article', article);
  const storeLabel = storeKey === null ? '' : withCode(t, { code: storeKey, name: storeName, reason: null });
  const articleLabel = article === null ? '' : withCode(t, { code: article, name: articleName, reason: null });

  const ancestors: DrillCrumb[] = [];
  if (departmentFamily !== null && storeKey !== null) {
    ancestors.push({ label: familyLabel(departmentFamily), href: drillHref(departmentPath(departmentFamily), runId) });
  }
  if (storeKey !== null && article !== null) {
    ancestors.push({ label: storeLabel, href: drillHref(storePath(storeKey), runId, departmentFamily), mono: storeName === null });
  }
  if (storeKey !== null && article !== null && leafKey !== null) {
    ancestors.push({ label: articleLabel, href: drillHref(articlePath(storeKey, article), runId, departmentFamily), mono: articleName === null });
  }

  const entity = leafKey !== null && article !== null
    ? { name: t(leafKey), code: article }
    : article !== null
      ? { name: articleName === null ? null : articleLabel, code: article }
      : storeKey !== null
        ? { name: storeName === null ? null : storeLabel, code: storeKey }
        : { name: departmentFamily === null ? null : familyLabel(departmentFamily), code: departmentFamily ?? '' };
  const kickerKey = article !== null
    ? DRILL_KICKER_KEYS.storeArticle
    : storeKey !== null ? DRILL_KICKER_KEYS.store : DRILL_KICKER_KEYS.department;
  const parent = leafKey !== null && article !== null
    ? t('inseason.drill.article', { article: articleLabel })
    : article !== null && storeKey !== null
      ? t('inseason.drill.store', { store: storeLabel })
      : storeKey !== null && departmentFamily !== null
        ? t('inseason.drill.department', { family: familyLabel(departmentFamily) })
        : null;

  return (
    <DrillHeader
      origin={{ app: t('nav.app.replenishment'), page: t(ALLOCATION_NAV_LABEL_KEY), href: drillHref(ALLOCATION_PATH, runId) }}
      ancestors={ancestors}
      entity={entity}
      kickerKey={kickerKey}
      constants={[
        { id: 'market', labelKey: DRILL_CONSTANT_KEYS.market, value: market ?? t('absent.notStated') },
        { id: 'run', labelKey: DRILL_CONSTANT_KEYS.run, value: runId === null ? t('absent.notStated') : <RunWords runId={runId} /> },
        ...(parent === null ? [] : [{ id: 'parent', labelKey: DRILL_CONSTANT_KEYS.parent, value: parent }]),
      ]}
    />
  );
}
